import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateActualExpenseInput,
  type ActualExpense,
  type ActualExpenseInput,
} from "../../domain/actualExpense";
import type { FinancialBucket } from "../../domain/buckets";
import type { CostType } from "../../domain/proposedCostLine";
import { ValidationError } from "./projectRepository";

interface ExpenseRow {
  id: string;
  project_id: string;
  date: string;
  description: string;
  vendor: string;
  category_id: string;
  amount_cents: number;
  tax_cents: number;
  bucket_override: string | null;
  linked_proposed_line_id: string | null;
  feature_id: string | null;
  cost_type: string | null;
  notes: string;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapExpense(row: ExpenseRow): ActualExpense {
  return {
    id: row.id,
    projectId: row.project_id,
    date: row.date,
    description: row.description,
    vendor: row.vendor,
    categoryId: row.category_id,
    amountCents: Number(row.amount_cents),
    taxCents: Number(row.tax_cents),
    bucketOverride: row.bucket_override as FinancialBucket | null,
    linkedProposedLineId: row.linked_proposed_line_id,
    linkedFeatureId: row.feature_id,
    costType: (row.cost_type as CostType | null) ?? null,
    notes: row.notes,
    voidedAt: row.voided_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const SELECT = `SELECT id, project_id, date, description, vendor, category_id,
                       amount_cents, tax_cents, bucket_override,
                       linked_proposed_line_id, feature_id, cost_type, notes, voided_at,
                       created_at, updated_at
                  FROM actual_expenses`;

export class ExpenseRepository {
  constructor(private readonly db: DbExecutor) {}

  /** All expenses for a project, voided included (callers filter for display). */
  async listByProject(projectId: string): Promise<ActualExpense[]> {
    const rows = await this.db.select<ExpenseRow>(
      `${SELECT} WHERE project_id = ? ORDER BY date DESC, created_at DESC`,
      [projectId],
    );
    return rows.map(mapExpense);
  }

  /** Direct links must reference records in the same project; the domain
   *  validator already rejects setting both direct links at once. */
  private async assertLinkValid(
    projectId: string,
    linkedLineId: string | null,
    linkedFeatureId: string | null,
  ): Promise<void> {
    if (linkedLineId !== null) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM proposed_cost_lines WHERE id = ?",
        [linkedLineId],
      );
      if (rows.length === 0) {
        throw new ValidationError(["The linked proposed item no longer exists."]);
      }
      if (rows[0].project_id !== projectId) {
        throw new ValidationError([
          "An expense can only link to a proposed item in the same project.",
        ]);
      }
    }
    if (linkedFeatureId !== null) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM project_features WHERE id = ?",
        [linkedFeatureId],
      );
      if (rows.length === 0) {
        throw new ValidationError(["The linked feature no longer exists."]);
      }
      if (rows[0].project_id !== projectId) {
        throw new ValidationError([
          "An expense can only link to a feature in the same project.",
        ]);
      }
    }
  }

  async create(projectId: string, input: ActualExpenseInput): Promise<ActualExpense> {
    const problems = validateActualExpenseInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.assertLinkValid(
      projectId,
      input.linkedProposedLineId,
      input.linkedFeatureId ?? null,
    );
    const now = new Date().toISOString();
    const expense: ActualExpense = {
      id: generateId(),
      projectId,
      date: input.date,
      description: input.description.trim(),
      vendor: input.vendor.trim(),
      categoryId: input.categoryId,
      amountCents: input.amountCents,
      taxCents: input.taxCents,
      bucketOverride: input.bucketOverride,
      linkedProposedLineId: input.linkedProposedLineId,
      linkedFeatureId: input.linkedFeatureId ?? null,
      costType: input.costType ?? null,
      notes: input.notes,
      voidedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO actual_expenses
         (id, project_id, date, description, vendor, category_id, amount_cents,
          tax_cents, bucket_override, linked_proposed_line_id, feature_id, cost_type,
          notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        expense.id,
        expense.projectId,
        expense.date,
        expense.description,
        expense.vendor,
        expense.categoryId,
        expense.amountCents,
        expense.taxCents,
        expense.bucketOverride,
        expense.linkedProposedLineId,
        expense.linkedFeatureId,
        expense.costType ?? null,
        expense.notes,
        expense.createdAt,
        expense.updatedAt,
      ],
    );
    return expense;
  }

  async update(id: string, input: ActualExpenseInput): Promise<void> {
    const problems = validateActualExpenseInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const rows = await this.db.select<{ project_id: string }>(
      "SELECT project_id FROM actual_expenses WHERE id = ?",
      [id],
    );
    if (rows.length === 0) {
      throw new Error(`Expense ${id} not found.`);
    }
    await this.assertLinkValid(
      rows[0].project_id,
      input.linkedProposedLineId,
      input.linkedFeatureId ?? null,
    );
    await this.db.execute(
      `UPDATE actual_expenses SET
         date = ?, description = ?, vendor = ?, category_id = ?, amount_cents = ?,
         tax_cents = ?, bucket_override = ?, linked_proposed_line_id = ?,
         feature_id = ?, cost_type = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.date,
        input.description.trim(),
        input.vendor.trim(),
        input.categoryId,
        input.amountCents,
        input.taxCents,
        input.bucketOverride,
        input.linkedProposedLineId,
        input.linkedFeatureId ?? null,
        input.costType ?? null,
        input.notes,
        new Date().toISOString(),
        id,
      ],
    );
  }

  /**
   * Void: excludes the expense from every total while preserving the
   * historical record. Actual financial history is never physically deleted.
   */
  async void(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE actual_expenses SET voided_at = ?, updated_at = ? WHERE id = ? AND voided_at IS NULL",
      [now, now, id],
    );
  }

  async unvoid(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE actual_expenses SET voided_at = NULL, updated_at = ? WHERE id = ?",
      [now, id],
    );
  }
}
